'use client';

import React from 'react';
import s from './ConnectionsPage.module.scss';
import Section from '../../components/Section/Section';
import CardImageItem from '../../components/CardImageItem';
import { t } from '../../i18n';
import Grid from '../../components/Grid/Grid';
import Box from '../../components/Box/Box';
import TabbedTextPanel from '../../components/TabbedTextPanel/TabbedTextPanel';
import CompanyCardLinks from '../../components/CompanyCardLinks/CompanyCardLinks';
import ProfileCardLink from '../../components/ProfileCardLink/ProfileCardLink';
import ReportItem from '../../components/ReportItem/ReportItem';
import RelatedCard from '../../components/RelatedCard/RelatedCard';
import ProfileCardFilterList from '../../components/ProfileCardFilterList/ProfileCardFilterList';
import FeaturesCard from '../../components/FeaturesCard/FeaturesCard';
import IconText from '../../components/IconText/IconText';
import useSWR, { useSWRConfig } from 'swr';
import { useIndividualsFilters } from './hooks/useIndividualsFilters';
import classNames from 'classnames';
import SkeletonLoader from '../../components/SkeletonLoader/SkeletonLoader';
import Button from '../../components/Button/Button';
import * as linkify from 'linkifyjs';
import linkifyHtml from 'linkify-html';
import { keysToCamelFromSnake } from '../../utils/caseconverters';

export const IndividualTab = ({
    features = {},
    selectedCompany,
    individuals = [],
    individualProfile,
    holdings = [],
    relativeUrl = '',
    disabled,
    urlData,
    mediaMonitorRootUrl = '',
    onClearCompany,
    isClearingCompany = false,
}) => {
    const hasProfile = !!individualProfile?.name;

    return (
        <Section noMarginBottom isMainSection fullHeight>
            <div className={s.LayoutWrapper}>
                {hasProfile ? (
                    <IndividualProfile
                        {...individualProfile}
                        holdings={holdings}
                        relativeUrl={relativeUrl}
                        urlData={urlData}
                        mediaMonitorRootUrl={mediaMonitorRootUrl}
                    />
                ) : (
                    <ProfileCardFilterList
                        individuals={individuals}
                        disabled={disabled}
                        selectedCompany={selectedCompany}
                        holdings={holdings}
                        onClearCompany={onClearCompany}
                        isClearingCompany={isClearingCompany}
                    />
                )}
            </div>
        </Section>
    );
};
const IndividualProfile = ({
    name,
    keyRole,
    image,
    sectors = [],
    bioTopline,
    career = [],
    earlyLifeAndEducation = [],
    personalLife = [],
    otherRoles = [],
    policies = [],
    notes = [],
    controversies = [],
    sanctions = [],
    seeAlso = [],
    primaryKey_,
    companyNames = [],
    companyIds = [],
    relatedCompanies = [],
    relatedArticles = [],
    politicalAffiliation = [],
    holdings = [],
    relativeUrl,
    urlData,
    mediaMonitorRootUrl = '',
}) => {
    const biographyTabs = [
        {
            id: 'topline',
            title: t('biography.topline'),
            text: {
                title: t('biography.topline'),
                content: bioTopline,
            },
        },
        ...(otherRoles.length > 0
            ? [
                  {
                      id: 'currentRoles',
                      title: t('biography.currentRoles'),
                      text: {
                          title: t('biography.currentRoles'),
                          content: (
                              <BiographyRolesTab
                                  roles={otherRoles}
                                  holdings={holdings}
                                  relativeUrl={relativeUrl}
                              />
                          ),
                      },
                  },
              ]
            : []),
        {
            id: 'education',
            title: t('biography.earlyLifeAndEducation'),
            text: {
                title: t('biography.earlyLifeAndEducation'),
                content: earlyLifeAndEducation,
            },
        },
        {
            id: 'career',
            title: t('biography.career'),
            text: {
                title: t('biography.career'),
                content: career,
            },
        },
        {
            id: 'personal',
            title: t('biography.personalLife'),
            text: {
                title: t('biography.personalLife'),
                content: personalLife,
            },
        },
        {
            id: 'controversies',
            title: t('biography.controversies'),
            text: {
                title: t('biography.controversies'),
                content: controversies,
            },
        },
        {
            id: 'policies',
            title: t('biography.policies'),
            text: {
                title: t('biography.policies'),
                content: policies,
            },
        },
        {
            id: 'notes',
            title: t('biography.notes'),
            text: {
                title: t('biography.notes'),
                content: notes,
            },
        },
        {
            id: 'sanctions',
            title: t('biography.sanctions'),
            text: {
                title: t('biography.sanctions'),
                content: sanctions,
            },
        },
        {
            id: 'seeAlso',
            title: t('biography.seeAlso'),
            text: {
                title: t('biography.seeAlso'),
                content: linkifyHtml(seeAlso.join('\n\n'), {
                    target: '_blank',
                    attributes: {
                        rel: 'noopener noreferrer',
                    },
                }),
            },
        },
    ].filter((tab) => {
        const content = tab.text?.content;
        return typeof content === 'string'
            ? content.trim().length > 0
            : !!content;
    });

    // Resolved server-side by source_id, so companies outside the dashboard's
    // connections countries are included — they link out even though they are
    // absent from the country and holding dropdowns.
    const relatedHoldings = relatedCompanies;

    const fetcher = (url) =>
        fetch(url)
            .then((res) => res.json())
            .then((json) => keysToCamelFromSnake(json));

    const {
        data: relatedNews = { data: [] },
        error: newsError,
        isLoading: newsIsLoading,
    } = useSWR(
        `/wt/fenix/connections/news/?individualId=${primaryKey_}&${new URLSearchParams(urlData).toString()}`,
        fetcher
    );

    const {
        data: relatedPeople = { data: [] },
        error,
        isLoading,
    } = useSWR(
        `/wt/fenix/connections/people/?holding=${companyIds.join(',')}&${new URLSearchParams(urlData).toString()}&individualId=${primaryKey_}&relatedPeople=true`,
        fetcher
    );
    const isSanctioned = sanctions.length > 0;
    // const politicalAffiliation = ['Humane products'];
    const firstPoliticalAffiliation = !!politicalAffiliation.name
        ? politicalAffiliation
        : '';

    const pathname =
        typeof window !== 'undefined' ? window.location.pathname : '';
    const relativePath = pathname.replace(/\/individual\/?$/, '');

    return (
        <div className={s.IndividualTabWrapper}>
            <div className={s.IndividualTabHeader}>
                {name && (
                    <CardImageItem
                        image={image}
                        title={keyRole}
                        value={name}
                        hasEditorialImage={true}
                        isSquare={true}
                        modifiers={['IndividualTab']}
                        isSanctioned={isSanctioned}
                    />
                )}
                {!!firstPoliticalAffiliation.name && (
                    <CardImageItem
                        title={t(
                            'connectionsPage.holdings.politicalAffiliation'
                        )}
                        value={firstPoliticalAffiliation.name}
                        image={firstPoliticalAffiliation.image}
                        hasEditorialImage={true}
                        isSquare={true}
                    />
                )}
                {sectors.length > 0 && (
                    <div className={s.IndividualTabSectors}>
                        <h2 className={s.IndividualTabTitle}>
                            {t('connectionsPage.holdings.sectors')}
                        </h2>
                        <ul className={s.IndividualTabSectorsList}>
                            {sectors.map((sector, index) => (
                                <li
                                    key={index}
                                    className={s.IndividualTabSector}>
                                    {sector}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>
            <div className={s.IndividualTabContainer}>
                <Grid modifier="WithGapXLarge" columns={1}>
                    {biographyTabs.length > 0 && (
                        <Box
                            modifiers={['ConnectionsPage', 'Grey']}
                            title={t('connectionsPage.holdings.biography')}>
                            <TabbedTextPanel tabs={biographyTabs} />
                        </Box>
                    )}
                    {relatedHoldings.length > 0 && (
                        <Box
                            modifiers={[
                                'ConnectionsPage',
                                'Grey',
                                'NoPaddingRight',
                            ]}
                            title={t(
                                'connectionsPage.holdings.relatedCompanies'
                            )}>
                            <div
                                className={classNames(s.FlexWrapContainer, {
                                    [s.Overflow]: relatedHoldings.length > 1,
                                })}>
                                {relatedHoldings.map((company, index) => {
                                    const countryIso =
                                        company.countryCode?.toLowerCase();

                                    const companyPageLink = `${relativeUrl}${countryIso}/?holding=${company.sourceId}`;
                                    const relatedIndividualsPageLink = `${relativeUrl}${countryIso}/individual/?holding=${company.sourceId}`;
                                    return (
                                        <CompanyCardLinks
                                            isWhite
                                            key={index}
                                            title={company.name}
                                            id={company.sourceId}
                                            revenue={company.revenue}
                                            employees={company.employeeCount}
                                            incorporated={company.foundedDate}
                                            companyPageLink={companyPageLink}
                                            relatedIndividualsPageLink={
                                                relatedIndividualsPageLink
                                            }
                                        />
                                    );
                                })}
                            </div>
                        </Box>
                    )}
                    {(isLoading || relatedPeople.data.length > 0) &&
                        relatedPeople.data.filter(
                            (person) => !person.properties.hidden
                        ).length > 0 && (
                            <Box
                                modifiers={[
                                    'ConnectionsPage',
                                    'Grey',
                                    'NoPaddingRight',
                                ]}
                                title={t(
                                    'connectionsPage.holdings.relatedIndividuals'
                                )}>
                                <div
                                    className={classNames(s.FlexWrapContainer, {
                                        [s.Overflow]:
                                            relatedPeople.data.filter(
                                                (person) =>
                                                    !person.properties.hidden
                                            ).length > 1,
                                    })}>
                                    {isLoading
                                        ? Array.from({ length: 3 }).map(
                                              (_, index) => (
                                                  <div
                                                      key={index}
                                                      className={
                                                          s.SkeletonLoader
                                                      }>
                                                      <SkeletonLoader size="cover" />
                                                  </div>
                                              )
                                          )
                                        : relatedPeople.data.map(
                                              (individual, index) =>
                                                  !individual.properties
                                                      .hidden && (
                                                      <ProfileCardLink
                                                          key={index}
                                                          isWhite
                                                          {...individual.properties}
                                                      />
                                                  )
                                          )}
                                </div>
                            </Box>
                        )}
                    {(newsIsLoading || relatedNews.data.length > 0) && (
                        <Box
                            modifiers={[
                                'ConnectionsPage',
                                'Grey',
                                'NoPaddingRight',
                            ]}
                            title={t(
                                'connectionsPage.holdings.relatedArticles'
                            )}>
                            <div
                                className={classNames(s.FlexWrapContainer, {
                                    [s.Overflow]: relatedNews.data.length > 1,
                                })}>
                                {newsIsLoading
                                    ? Array.from({ length: 3 }).map(
                                          (_, index) => (
                                              <div
                                                  key={index}
                                                  className={s.SkeletonLoader}>
                                                  <SkeletonLoader size="cover" />
                                              </div>
                                          )
                                      )
                                    : relatedNews.data.map((article, index) => {
                                          const url =
                                              mediaMonitorRootUrl +
                                              article.properties.newsId;
                                          return (
                                              <RelatedCard
                                                  key={index}
                                                  {...article.properties}
                                                  isReport={false}
                                                  url={url}
                                              />
                                          );
                                      })}
                            </div>
                        </Box>
                    )}
                </Grid>
            </div>
        </div>
    );
};

const BiographyRolesTab = ({ roles = [], holdings = [], relativeUrl = '' }) => {
    return (
        <div className={s.RolesWrapper}>
            {roles.map((roleStr, index) => {
                const parts = roleStr.split(',');
                const company = parts.pop().trim();
                const role = parts.join(',').trim();

                const matchingHolding = holdings.find((h) =>
                    h.name?.toLowerCase().includes(company.toLowerCase())
                );

                const companyPageLink = matchingHolding
                    ? `${relativeUrl}${matchingHolding.countryCode?.toLowerCase()}/?holding=${matchingHolding.sourceId}`
                    : null;

                return (
                    <div key={index} className={s.RoleCard}>
                        <div className={s.RoleTitleWrapper}>
                            <div className={s.RoleTitle}>{company}</div>
                            {companyPageLink && (
                                <Button
                                    title={t('biography.goToCompany')}
                                    modifier="Secondary"
                                    className={s.GoToButton}
                                    url={companyPageLink}
                                />
                            )}
                        </div>
                        <div className={s.RoleDescription}>{role}</div>
                    </div>
                );
            })}
        </div>
    );
};
